import React, { useCallback, useEffect, useState } from 'react';
import { EMPTY_VALUE } from '../../constants';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useTranslation } from '../../contexts/translation-context';
import { useError } from '../../field-error';
import { useFieldAttributes } from '../../services/field-attributes';
import { FileUploadDetails, useFileUpload } from '../autocomplete/wut/FileUpload';
import { FieldDescriptionWUT } from '../field-description';
import { FieldLabelWUT } from '../field-label';
import { FieldErrorWUT } from '../field-error';
import { useUploadFile } from './use-upload-file';
import { FORM_STATUS, useFormStatus } from '../../contexts/form-status-provider';
import { FileInputWUT } from '../../inputs/file-input-wut/file-input-wut';
export const FileFormatMap = {
  Video: 'video/*',
  Image: 'image/*',
  Audio: 'audio/*',
  Document: '.ai, .cdr, .csv, .doc, .docb, .docx, .dot, .dotx, .dwg, .eps, .epub, .fla, .gpx, .ical, .icalendar, .ics, .ifb, .indd, .ipynb, .key, .kml, .kmz, .mobi, .mtf, .mtx, .numbers, .odg, .odp, .ods, .odt, .otp, .ots, .ott, .oxps, .pages, .pdf, .pdn, .pkg, .pot, .potx, .pps, .ppsx, .ppt, .pptx, .psd, .pub, .rtf, .sldx, .txt, .json, .vcf, .xcf, .xls, .xlsx, .xlt, .xltx, .xlw, .xps,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel',
  Archive: '.zip, .rar, .tar, .tar.gz, .gz, .gzip, .jar, .7z, .fgz, .webarchive'
};
function convertValuesToFiles(_value) {
  if (_value === void 0) {
    _value = [];
  }
  const value = _value ? _value : [];
  try {
    return value.map(v => {
      return {
        file: new File([new Int8Array(0)], v.displayName, {
          type: v.fileType
        }),
        state: 'uploaded'
      };
    });
  } catch (e) {
    console.error(e);
  }
  return [];
}
export function useFileUploadController(valueSliderLimit, value, explanationText, target, onChange, uploadFileFormats) {
  const [files, setFiles] = useState([]);
  const [_error, setError] = useState();
  const uploadFiles = useUploadFile();
  const {
    t
  } = useTranslation();
  const forcedState = useForcedState();
  const hover = forcedState.includes(ForcedState.BUTTONS_HOVER);
  const validateFilesLimit = useCallback(filesCount => {
    setError(filesCount > valueSliderLimit ? t(`form.file-upload.error.limit`, {
      limit: valueSliderLimit
    }) : undefined);
  }, [valueSliderLimit, t]);
  const updateFiles = useCallback(uploadedFiles => {
    setFiles(uploadedFiles);
    validateFilesLimit(uploadedFiles.length);
  }, [validateFilesLimit]);
  useEffect(() => {
    if (value || value === EMPTY_VALUE) {
      const errorFiles = files.filter(f => f.state === 'error');
      updateFiles(convertValuesToFiles(value).concat(errorFiles));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- https://wix.slack.com/archives/CQF1SC6MN/p1713873328075449
  }, [value]);
  const getExplanationText = useCallback(() => {
    if (!files.length) {
      return explanationText;
    }
    let _explanationText = explanationText || t(`form.file-upload.explanation-text`, {
      count: files.length
    });
    const uploadingCount = files.filter(f => f.state === 'uploading').length;
    if (uploadingCount) {
      const uploadedCount = files.filter(f => f.state === 'uploaded' && f.file.size > 0).length;
      const progress = uploadedCount / (uploadedCount + uploadingCount);
      _explanationText += ' ' + t(`form.file-upload.uploading`, {
        count: Math.round(progress * 100)
      });
    }
    return _explanationText;
  }, [files, explanationText, t]);
  const {
    setFormStatus
  } = useFormStatus();
  const _uploadFiles = useCallback(async filesToUpload => {
    setFormStatus(target, FORM_STATUS.BUSY);
    const newUploadedFiles = await uploadFiles(filesToUpload, file => {
      setFiles(previousFiles => previousFiles.map(f => f.file === file ? {
        file,
        state: 'uploaded'
      } : f));
    }, (_err, file) => {
      setFiles(previousFiles => previousFiles.map(f => f.file === file ? {
        file,
        state: 'error',
        errorMessage: t('form.file-upload.error.upload-failed')
      } : f));
    });
    onChange == null || onChange((value || []).concat(newUploadedFiles));
    setFormStatus(target, FORM_STATUS.READY);
  }, [setFormStatus, uploadFiles, onChange, value, t, target]);
  const handleChange = async _files => {
    const filesArr = Array.from(_files);
    const end = valueSliderLimit - files.length;
    const filesToUpload = filesArr.slice(0, end);
    const filesToAdd = filesToUpload.map((_file, _index) => {
      return {
        file: _file,
        state: 'uploading'
      };
    });
    const excessFiles = filesArr.slice(end).map((_file, _index) => {
      return {
        file: _file,
        state: 'error'
      };
    });
    updateFiles(files.concat(filesToAdd, excessFiles));
    await _uploadFiles(filesToAdd);
  };
  const handleDelete = index => {
    const fileToDelete = files[index];
    if (value && onChange && fileToDelete.state !== 'error') {
      const filteredValues = value.filter(f => f.displayName !== fileToDelete.file.name);
      onChange(filteredValues);
    } else {
      const filteredFiles = files.filter(f => f !== fileToDelete);
      updateFiles(filteredFiles);
    }
  };
  const accept = uploadFileFormats.map(format => FileFormatMap[format]).join();
  const {
    fileInputRef,
    textBelowButtonRef,
    fileErrorMessage
  } = useFileUpload({
    files
  });
  return {
    files,
    _error,
    hover,
    getExplanationText,
    handleChange,
    handleDelete,
    accept,
    fileInputRef,
    textBelowButtonRef,
    fileErrorMessage
  };
}
export const FileUpload = _ref => {
  let {
    id,
    target,
    label,
    description,
    value,
    onChange,
    onFocus,
    onBlur,
    error,
    customErrors,
    disabled = false,
    buttonText,
    uploadFileFormats,
    valueSliderLimit,
    required,
    hideLabel,
    hideAsterisk,
    explanationText,
    FieldLayout,
    requiredIndicatorType,
    requiredIndicatorPlacement,
    fieldType
  } = _ref;
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy
  } = useFieldAttributes(id, error, description);
  const {
    hasError,
    errorMessage
  } = useError(error, customErrors, fieldType);
  const {
    files,
    _error,
    hover,
    getExplanationText,
    handleChange,
    handleDelete,
    accept,
    fileInputRef,
    textBelowButtonRef,
    fileErrorMessage
  } = useFileUploadController(valueSliderLimit, value, explanationText, target, onChange, uploadFileFormats);
  const _errorMessage = fileErrorMessage || _error || errorMessage;
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      htmlFor: inputId,
      required: required,
      label: label,
      hideLabel: hideLabel,
      requiredIndicatorHidden: hideAsterisk,
      requiredIndicatorType: requiredIndicatorType,
      requiredIndicatorPlacement: requiredIndicatorPlacement
    }),
    renderInput: () => /*#__PURE__*/React.createElement(FileInputWUT, {
      onFocus: onFocus,
      onBlur: onBlur,
      disabled: disabled,
      buttonText: buttonText,
      valueSliderLimit: valueSliderLimit,
      required: required,
      inputId: inputId,
      ariaDescribedBy: ariaDescribedBy,
      files: files,
      hover: hover,
      handleChange: handleChange,
      accept: accept,
      fileInputRef: fileInputRef,
      textBelowButtonRef: textBelowButtonRef,
      hasError: hasError
    }),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FileUploadDetails, {
      fileInputRef: fileInputRef,
      textBelowButtonRef: textBelowButtonRef,
      files: files,
      onDeleteClick: handleDelete,
      hasError: Boolean(_errorMessage),
      explanationText: getExplanationText()
    }), /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: Boolean(_errorMessage),
      errorMessage: _errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }))
  });
};
export default FileUpload;
//# sourceMappingURL=file-upload.js.map